/**
 * Advanced LLM Testing Component
 * Tests LLM chat completions with external API
 */
import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const AdvancedLLMTesting = () => {
  const { user } = useAuth();
  const [apiToken, setApiToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Chat Interface
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [selectedModel, setSelectedModel] = useState("Qwen/Qwen3.8-27B-FP8");
  const [systemPrompt, setSystemPrompt] = useState(
    "Aucune consigne particulière"
  );
  const [profileType, setProfileType] = useState("standard");
  const [maxTokens, setMaxTokens] = useState(null);
  const [temperature, setTemperature] = useState(1.0);
  const [enableStreaming, setEnableStreaming] = useState(false);

  // In development, auth goes to :3030 and AI/LLM goes to :8080
  const getAPIUrl = () => {
    return (
      import.meta.env.VITE_TRANSCRIPTION_API_URL || "http://localhost:8000"
    );
  };

  const API_BASE_URL = getAPIUrl();
  const chatContainerRef = useRef(null);

  useEffect(() => {
    if (user?.token) {
      setApiToken(user.token);
    }
  }, [user]);

  const scrollToBottom = (behavior = "smooth") => {
    const container = chatContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  };

  useEffect(() => {
    const behavior = messages.length > 1 ? "smooth" : "auto";
    scrollToBottom(behavior);
  }, [messages]);

  const glassPanelClass =
    "rounded-3xl border border-white/10 bg-slate-950/60 backdrop-blur-3xl p-6 shadow-[0_30px_120px_rgba(2,6,23,0.65)]";
  const glassInsetClass =
    "rounded-2xl border border-white/5 bg-white/5 backdrop-blur-2xl";
  const glassInputClass =
    "w-full rounded-xl border border-white/15 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/60 focus:border-transparent";
  const badgeClass =
    "inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[0.65rem] uppercase tracking-[0.35em] text-slate-200/70";

  const addResult = (
    testName,
    success,
    data,
    error = null,
    duration = null
  ) => {
    const result = {
      id: Date.now() + Math.random(),
      testName,
      success,
      data,
      error,
      duration,
      timestamp: new Date().toLocaleTimeString(),
    };
    setTestResults((prev) => [result, ...prev.slice(0, 19)]); // Keep last 20 results
  };

  const authenticatedFetch = async (url, options = {}) => {
    if (!apiToken) {
      throw new Error("Le token API est requis");
    }

    return fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${apiToken}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
    });
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || !apiToken) {
      return;
    }

    const userMessage = { role: "user", content: currentMessage.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setCurrentMessage("");
    setLoading(true);

    // Prepare API request
    const requestMessages = [];
    if (systemPrompt.trim()) {
      requestMessages.push({ role: "system", content: systemPrompt.trim() });
    }
    requestMessages.push(...newMessages);

    const requestPayload = {
      messages: requestMessages,
      model: selectedModel,
      profile_type: profileType,
      temperature: temperature,
      stream: enableStreaming,
    };

    // Only include max_tokens if specified
    if (maxTokens) {
      requestPayload.max_tokens = maxTokens;
    }

    const startTime = Date.now();

    try {
      if (enableStreaming) {
        // Handle streaming response
        const response = await authenticatedFetch(
          `${API_BASE_URL}/llm/chat/completions`,
          {
            method: "POST",
            body: JSON.stringify(requestPayload),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "La requete streaming a echoue");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        // Add empty assistant message first
        setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

        let accumulatedContent = "";
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          const lines = buffer.split("\n");

          // Keep the last incomplete line in the buffer
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ") && !line.includes("[DONE]")) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.choices && data.choices[0].delta.content) {
                  accumulatedContent += data.choices[0].delta.content;
                  setMessages((prev) => {
                    const updated = [...prev];
                    updated[updated.length - 1].content = accumulatedContent;
                    return updated;
                  });
                }
              } catch (e) {
                // Ignore parsing errors for streaming chunks
              }
            }
          }
        }

        const duration = Date.now() - startTime;
        addResult(
          "Chat streaming",
          true,
          "Flux termine",
          null,
          `${duration}ms`
        );
      } else {
        // Handle regular response
        const response = await authenticatedFetch(
          `${API_BASE_URL}/llm/chat/completions`,
          {
            method: "POST",
            body: JSON.stringify(requestPayload),
          }
        );

        const duration = Date.now() - startTime;

        if (response.ok) {
          const data = await response.json();
          const assistantMessage = data.choices[0].message;
          setMessages((prev) => [...prev, assistantMessage]);
          addResult("Chat Completion", true, data, null, `${duration}ms`);
        } else {
          const errorData = await response.json();
          addResult(
            "Chat Completion",
            false,
            null,
            errorData.detail,
            `${duration}ms`
          );
        }
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      addResult("Chat Completion", false, null, error.message, `${duration}ms`);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setTestResults([]);
  };

  return (
    <div className="space-y-6 text-white">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="space-y-6">
          <section className={`${glassPanelClass} space-y-4`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                  Token d'acces
                </p>
                <h3 className="text-xl font-semibold text-white">
                  Authentification
                </h3>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs ${
                  apiToken
                    ? "bg-emerald-400/20 text-emerald-100"
                    : "bg-rose-500/30 text-rose-100"
                }`}
              >
                {apiToken ? "Token synchronise" : "Token manquant"}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type={showToken ? "text" : "password"}
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder="Entrez votre token API (epv_...)"
                className={glassInputClass}
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 transition hover:bg-white/10"
              >
                {showToken ? "Masquer" : "Afficher"}
              </button>
            </div>
            <p className="text-xs text-white/60">
              Les tokens se synchronisent depuis votre session. Collez une cle
              scopee pour forcer un test.
            </p>
          </section>

          <section className={`${glassPanelClass} space-y-5`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                  Orchestration
                </p>
                <h3 className="text-xl font-semibold text-white">
                  Configuration du chat
                </h3>
                <p className="text-xs text-white/60">
                  Ajustez le comportement runtime avant d'envoyer les prompts.
                </p>
              </div>
              <span className="rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs capitalize text-white/80">
                {profileType}
              </span>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.3em] text-white/60">
                Modele
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className={`${glassInputClass} bg-slate-900/80`}
              >
                <option value="Qwen/Qwen3.8-27B-FP8">Qwen 3.8 27B FP8</option>
                <option value="llama-3.3-70b-instruct">Llama 3.3 70B</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.3em] text-white/60">
                Type de profil
              </label>
              <select
                value={profileType}
                onChange={(e) => setProfileType(e.target.value)}
                className={`${glassInputClass} bg-slate-900/80 capitalize`}
              >
                <option value="standard">Standard</option>
                <option value="creative">Creatif</option>
                <option value="precise">Precis</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.3em] text-white/60">
                Prompt systeme
              </label>
              <textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Aucune consigne particulière"
                rows={4}
                className={`${glassInputClass} min-h-[110px] resize-none`}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.3em] text-white/60">
                  Tokens max
                </label>
                <input
                  type="number"
                  min="0"
                  max="8192"
                  value={maxTokens || ""}
                  onChange={(e) =>
                    setMaxTokens(
                      e.target.value ? parseInt(e.target.value, 10) : null
                    )
                  }
                  placeholder="Illimite"
                  className={glassInputClass}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-[0.3em] text-white/60">
                  Temperature {temperature.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  className="w-full accent-cyan-400"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-white/80">Mode streaming</span>
              <button
                type="button"
                onClick={() => setEnableStreaming((prev) => !prev)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition ${
                  enableStreaming
                    ? "border-cyan-400/50 bg-cyan-500/20 text-cyan-100"
                    : "border-white/10 bg-white/5 text-white/70"
                }`}
              >
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    enableStreaming ? "bg-cyan-300" : "bg-white/40"
                  }`}
                />
                {enableStreaming ? "Active" : "Desactive"}
              </button>
            </div>

            <button
              type="button"
              onClick={clearChat}
              className="w-full rounded-2xl border border-white/10 bg-gradient-to-r from-slate-800/80 to-slate-900/80 px-4 py-2 text-sm font-medium text-white transition hover:border-white/30"
            >
              Reinitialiser la session
            </button>
          </section>
        </div>

        <div className="xl:col-span-2 space-y-6">
          <section className={`${glassPanelClass} min-h-[520px] flex flex-col`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className={`${badgeClass}`}>Dialogue</p>
                <h3 className="mt-3 text-2xl font-semibold text-white">
                  Console temps reel
                </h3>
                <p className="text-sm text-white/70">
                  Les instructions sont envoyees vers {selectedModel} via
                  l'endpoint /llm/chat/completions.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                    loading
                      ? "bg-cyan-500/20 text-cyan-100"
                      : "bg-white/5 text-white/60"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      loading ? "bg-cyan-300 animate-pulse" : "bg-white/40"
                    }`}
                  />
                  {loading ? "Generation" : "Inactif"}
                </span>
                <span className="text-xs text-white/60">
                  {messages.length} message{messages.length === 1 ? "" : "s"}
                </span>
              </div>
            </div>

            <div className="mt-6 flex-1 overflow-hidden">
              <div
                ref={chatContainerRef}
                className={`${glassInsetClass} h-[22rem] overflow-y-auto p-4 space-y-4`}
              >
                {messages.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-center text-sm text-white/50">
                    Redigez un message ou ajoutez un prompt systeme pour
                    demarrer le dialogue.
                  </div>
                ) : (
                  messages.map((message, index) => {
                    const isUser = message.role === "user";
                    return (
                      <div
                        key={`${message.role}-${index}`}
                        className={`flex ${
                          isUser ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-full lg:max-w-2xl rounded-2xl border px-4 py-3 text-sm leading-relaxed shadow-xl ${
                            isUser
                              ? "border-transparent bg-gradient-to-r from-indigo-500/80 to-cyan-500/80 text-white"
                              : "border-white/10 bg-white/5 text-white/90"
                          }`}
                        >
                          {isUser ? (
                            <div className="whitespace-pre-wrap">
                              {message.content}
                            </div>
                          ) : (
                            <div className="prose prose-sm prose-invert max-w-none">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {message.content}
                              </ReactMarkdown>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {loading && (
                  <div className="flex justify-start">
                    <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-xs text-cyan-100">
                      L'IA compose une reponse...
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <div className={`${glassInsetClass} p-4 space-y-3`}>
                <textarea
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Tapez votre message puis appuyez sur Entree pour envoyer"
                  rows={3}
                  disabled={loading || !apiToken}
                  className={`${glassInputClass} min-h-[90px] resize-none border-white/10 bg-slate-900/70`}
                />
                <div className="flex flex-wrap items-center gap-3 text-xs text-white/50">
                  <span>Entree pour envoyer · Shift + Entree pour un saut de ligne</span>
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={loading || !currentMessage.trim() || !apiToken}
                    className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 px-5 py-2 text-sm font-semibold text-white transition hover:from-cyan-400 hover:to-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span>Envoyer</span>
                    <span aria-hidden>↗</span>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <section className={`${glassPanelClass} space-y-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className={`${badgeClass}`}>Telemetrie</p>
            <h3 className="mt-3 text-2xl font-semibold text-white">
              Journal des resultats de test
            </h3>
            <p className="text-sm text-white/70">
              Chaque appel enregistre la latence, des extraits de payload et le
              contexte d'erreur.
            </p>
          </div>
          <span className="rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs text-white/70">
            {testResults.length
              ? `${testResults.length} entrees`
              : "Aucune donnee"}
          </span>
        </div>

        <div className={`${glassInsetClass} mt-2 max-h-80 overflow-y-auto`}>
          {testResults.length === 0 ? (
            <div className="py-10 text-center text-sm text-white/60">
              Lancez un chat completion pour commencer a collecter la
              telemetrie.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {testResults.map((result) => (
                <div
                  key={result.id}
                  className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-white">
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${
                          result.success ? "bg-emerald-400" : "bg-rose-400"
                        }`}
                      />
                      {result.testName}
                    </div>
                    {result.error && (
                      <p className="mt-1 text-xs text-rose-200">
                        {result.error}
                      </p>
                    )}
                    {result.data && typeof result.data === "object" && (
                      <details className="mt-2 text-xs text-white/70">
                        <summary className="cursor-pointer text-white/70">
                          Voir les donnees de reponse
                        </summary>
                        <pre className="mt-2 max-h-40 overflow-y-auto rounded-xl bg-slate-950/80 p-3 text-[0.65rem] text-white/70">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                  <div className="text-xs text-white/60">
                    <div>{result.timestamp}</div>
                    {result.duration && <div>{result.duration}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdvancedLLMTesting;
